import { useSession } from '@/auth/useSession';
import { FormModal, SubmitProps } from '@/components/form-modal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/hooks/use-toast';
import { emptySelectOption } from '@/components/ui/multi-select-utils';
import {
  createCoachPauseReassignmentRequests,
  getCoachPauseReassignmentPreview,
  getPendingStudentGroupReassignmentRequests,
} from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import {
  getReassignmentRequestFormDefaultValues,
  ReassignmentRequestFormValues,
  reassignmentRequestFormSchema,
} from '@/lib/student-group-reassignment-utils';
import { ReassignmentRequestForm } from '@/routes/_layout/student-groups/-components/reassignment-request-form';
import { formatPlainDate } from '@airreading/common';
import {
  CoachSubStatus,
  StudentGroupReassignmentReason,
  StudentGroupReassignmentReasonLabelMap,
  studentGroupReassignmentReasonSchema,
} from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useMemo } from 'react';

function defaultReasonForPauseSubStatuses(subStatuses: CoachSubStatus[]): StudentGroupReassignmentReason {
  if (subStatuses.includes(CoachSubStatus.non_renewal_of_contract)) {
    return StudentGroupReassignmentReason.contract_termination_or_non_renewal;
  }
  return StudentGroupReassignmentReason.teacher_request;
}

export interface CoachPauseReassignmentModalProps {
  coachId: string;
  pauseSubStatuses: CoachSubStatus[];
  onClose: () => void;
}

export function CoachPauseReassignmentModal({ coachId, pauseSubStatuses, onClose }: CoachPauseReassignmentModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { actingUser } = useSession();

  const { data: preview, isPending: isPreviewPending } = useQuery(getCoachPauseReassignmentPreview(coachId));

  const { mutateAsync } = useMutation({
    mutationFn: createCoachPauseReassignmentRequests,
  });

  const defaultReason = defaultReasonForPauseSubStatuses(pauseSubStatuses);
  const defaultValues = useMemo(
    () =>
      getReassignmentRequestFormDefaultValues({
        requester: actingUser
          ? { value: actingUser.id, label: actingUser.fullName ?? actingUser.id, disable: false }
          : emptySelectOption,
        reassignmentReasons: [
          {
            value: defaultReason,
            label: StudentGroupReassignmentReasonLabelMap[defaultReason],
          },
        ],
      }),
    [actingUser, defaultReason],
  );

  const activeGroupCount = preview?.activeStudentGroupCount ?? 0;
  const lastCompletedLabel = preview?.lastCompletedSessionPlainDate
    ? formatPlainDate(preview.lastCompletedSessionPlainDate)
    : '—';

  const onSubmit = async ({ formData, closeModal }: SubmitProps<ReassignmentRequestFormValues>) => {
    try {
      const result = await mutateAsync({
        coachId,
        effectivePlainDate: formData.effectiveDate,
        requesterId: formData.requester.value,
        reasons: formData.reassignmentReasons.map(({ value }) => studentGroupReassignmentReasonSchema.parse(value)),
        preferredCoachId: formData.preferredNewTeacher.value.trim() || null,
        specialRequirements: formData.specialRequirements?.trim() || null,
      });

      const descriptionParts = [`${result.createdCount} reassignment request(s) created`];
      if (result.skippedAlreadyPendingCount > 0) {
        descriptionParts.push(`${result.skippedAlreadyPendingCount} skipped (pending request already existed)`);
      }
      if (result.skippedEffectiveDateAfterEndCount > 0) {
        descriptionParts.push(`${result.skippedEffectiveDateAfterEndCount} skipped (effective date after group end)`);
      }

      toast({
        title: 'Success',
        description: `${descriptionParts.join('. ')}.`,
      });

      await queryClient.invalidateQueries(getPendingStudentGroupReassignmentRequests());
      closeModal();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: airServerErrorFrom(error)?.userFacingMessage ?? 'Failed to create reassignment requests',
        variant: 'destructive',
      });
    }
  };

  return (
    <FormModal
      modalTitle="Create reassignment requests"
      formSchema={reassignmentRequestFormSchema}
      defaultValues={defaultValues}
      resetOnDefaultValuesChange
      onSubmit={onSubmit}
      onClose={onClose}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      trigger={<span className="sr-only">Create reassignment requests</span>}
      cancelBtnLabel="Skip reassignment"
      confirmBtnLabel="Create reassignment requests"
      initialOpen
      preventExternalClose
      dialogContentProps={{ className: 'max-w-lg lg:max-w-lg gap-3 p-4' }}
    >
      {({ form }) => (
        <div className="space-y-3 pb-4">
          <p className="text-sm text-muted-foreground">
            Opening requests for{' '}
            <span className="font-semibold text-foreground">
              {isPreviewPending ? '…' : `${activeGroupCount} student group${activeGroupCount === 1 ? '' : 's'}`}
            </span>
          </p>
          <p className="text-sm">
            <span className="text-muted-foreground">Last completed session date</span>{' '}
            <span className="font-medium">{isPreviewPending ? '…' : lastCompletedLabel}</span>
          </p>
          <ReassignmentRequestForm form={form} />
          <Alert variant="info" className="items-start">
            <Info className="h-4 w-4" />
            <AlertDescription>
              These settings will apply to all {isPreviewPending ? '…' : activeGroupCount} student group
              {activeGroupCount === 1 ? '' : 's'}. You can edit individual requests after they are created.
            </AlertDescription>
          </Alert>
        </div>
      )}
    </FormModal>
  );
}
